/* eslint-disable no-secrets/no-secrets */

import React, { useContext, useCallback, useState, useEffect, useMemo } from 'react';
import _ from 'lodash';
import { TranslatorContext } from '@jutro/locale';
import { DropdownMenuButton } from '@jutro/components';
import { wizardProps } from 'wmic-pe-portals-custom-wizard-react';
import { ViewModelServiceContext, ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import {
    CONSTANTS,
    WMICUserAccessUtil,
    WMICDriverUtil,
    WMICRichTextUtil,
    WMICVariousUtil,
    WMICLogger,
    MODAL_CONSTANTS,
    FlowStepId,
    WMICValidationUtil,
    useCanJumpToWizardStepDispatch,
    parseErrorMessage,
} from 'wmic-pe-portals-utils-js';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { WMICRequiredText } from 'wmic-pe-components-platform-react';
import { useWizardModals, WMICWizardSubmissionPage } from 'wmic-pe-portals-wizard-components-ui';
import { WMICDriverDetailView } from 'wmic-pe-capability-gateway-common-pa-react';

import { messages as quoteAndBindCommonMessages } from 'wmic-pe-capability-gateway-quoteandbind-common-react';
import { WMICErrorHandler } from 'wmic-pe-capability-quoteandbind-common-react';

import { DropdownMenuLink } from '@jutro/router';
import { RISK_PATHS } from 'wmic-pe-portals-utils-js/StringConstants';
import messages from './WMICDriversDetailsPage.messages';
import metadata from './WMICDriversDetailsPage.metadata.json5';

const { INSURED_ROLES } = CONSTANTS;

const ANIS_PATH = 'baseData.additionalNamedInsureds_WMIC';

function WMICDriversDetailsPage(props) {
    const { wizardData: submissionVM, updateWizardData } = props;
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useContext(TranslatorContext);
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { authHeader, authUserData } = useAuthentication();

    const [additionalNamedInsureds, setAdditionalNamedInsureds] = useState([]);
    const [skeletonStruct, setSkeletonStruct] = useState({});
    const [showErrors, setShowErrors] = useState(false);
    const driversWithDuplicateLicence = WMICDriverUtil.getDriversWithDuplicateLicense(
        _.get(submissionVM.value, RISK_PATHS.DRIVERS_PATH)
    );

    const { setWizardLoading, showConfirm, showError } = useWizardModals();
    const { initDispatch, wizardStepDispatch } = useCanJumpToWizardStepDispatch();

    const { onValidate, initialValidation, isComponentValid, registerInitialComponentValidation } =
        useValidation('paDriversDetailsPage');

    const isBMSi = _.get(submissionVM, 'isFromBMS_WMIC.value');

    useEffect(() => {
        submissionVM.value.flowStepId_WMIC = FlowStepId.PA_DRIVERS;
    }, [submissionVM.value]);

    const initDriverData = useCallback(async () => {
        initDispatch(submissionVM);
        const driversTmp = _.get(submissionVM, RISK_PATHS.DRIVERS_PATH);
        const additionalNamedInsuredsSub = _.get(submissionVM, `${ANIS_PATH}.value`);
        const additionalNamedInsuredsTmp = [];
        const skeletonStructure = await LoadSaveService.getSkeletonStructure(submissionVM.value, authHeader);
        // pull out driver specific structure
        const driverStruct = _.get(skeletonStructure, RISK_PATHS.DRIVERS_PATH);

        setSkeletonStruct(driverStruct);

        if (additionalNamedInsuredsSub && !_.isEmpty(additionalNamedInsuredsSub)) {
            additionalNamedInsuredsSub.forEach(nameInsured => {
                const additionalNameInsured = viewModelService.create(
                    nameInsured,
                    'pc',
                    'wmic.edge.ca.capabilities.policyjob.draft.dto.AdditionalNamedInsuredDTO_WMIC'
                );

                _.set(additionalNameInsured, 'isDriver', false);

                const aniSubType = _.get(additionalNameInsured, 'subtype.value.code');

                if (aniSubType === CONSTANTS.Person.toLowerCase()) {
                    const isDriver = driversTmp.value.some(
                        driver =>
                            _.get(additionalNameInsured, 'contactPublicID.value') ===
                            _.get(driver, 'person.publicID.value')
                    );

                    _.set(additionalNameInsured, 'isDriver', isDriver);
                }

                additionalNamedInsuredsTmp.push(additionalNameInsured);
            });
            if (!_.isEmpty(additionalNamedInsuredsTmp)) {
                setAdditionalNamedInsureds(additionalNamedInsuredsTmp);
            }
        }

        const primaryNamedInsuredTmp = _.get(submissionVM, 'baseData.primaryNamedInsured_WMIC.value');

        const isDriver = driversTmp.value.some(
            driver => _.get(primaryNamedInsuredTmp, 'contactPublicID') === _.get(driver, 'person.publicID')
        );

        _.set(primaryNamedInsuredTmp, 'isDriver', isDriver);
    }, [LoadSaveService, authHeader, submissionVM, viewModelService]);

    useEffect(() => {
        registerInitialComponentValidation(
            () =>
                _.get(submissionVM, RISK_PATHS.DRIVERS_PATH).value.length > 0 &&
                !WMICValidationUtil.hasDtoValidationErrors(submissionVM, FlowStepId.PA_DRIVERS)
        );

        initDriverData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const canEditSubmission = useMemo(
        () => WMICUserAccessUtil.canEditSubmission(authUserData.permission_Ext),
        [authUserData.permission_Ext]
    );

    const onCreateDriver = useCallback(
        (index, namedInsured, insuredRelation) => {
            if (isBMSi) {
                wizardStepDispatch({ type: 'riskAdded' });
            }

            const driver = WMICDriverUtil.createDriver(index, skeletonStruct, namedInsured, insuredRelation);
            const driversVM = _.get(submissionVM, RISK_PATHS.DRIVERS_PATH);
            const { _xCenter, _dtoName } = driversVM;
            const driverVM = viewModelService.create(driver, _xCenter, _dtoName, submissionVM.aspects.context());
            const commercialDriverVM = viewModelService.create(
                {},
                'pc',
                'wmic.edge.ca.capabilities.policyjob.lob.personalauto.coverables.dto.CommercialDriverDTO_WMIC'
            ).value;
            const isDriverPNI =
                submissionVM.baseData.primaryNamedInsured_WMIC.subtype.value === CONSTANTS.Person &&
                submissionVM.baseData.primaryNamedInsured_WMIC.publicID.value === driverVM.person.publicID.value;

            if (submissionVM.baseData?.isDriveChangeAvailable_Ext?.value) {
                const driveChangeVM = viewModelService.create(
                    {},
                    'pc',
                    'wmic.edge.ca.capabilities.policyjob.lob.personalauto.coverables.dto.DriveChangeDTO_WMIC'
                );

                if (isDriverPNI && submissionVM?.baseData?.primaryNamedInsured_WMIC?.driveChangeData_Ext?.value) {
                    const PNIdriveChangeData = submissionVM.baseData.primaryNamedInsured_WMIC.driveChangeData_Ext;

                    if (submissionVM.baseData.isDriveChangeFloowIntEnabled_Ext.value) {
                        driveChangeVM.isDriverAlreadyEnrolledInDriveChange.value =
                            PNIdriveChangeData.isAlreadyEnrolledWhenFloowIsActive.value;
                        driveChangeVM.isEmailEnrolled.value = PNIdriveChangeData.isEmailEnrolled.value;
                    } else {
                        driveChangeVM.isEnrolled.value = PNIdriveChangeData.isEnrolledInOtherPolicies.value;
                        driveChangeVM.previousDiscountPercentage.value = PNIdriveChangeData.priorDiscount.value;
                    }
                }

                driverVM.driveChange_WMIC = driveChangeVM.value;
            }

            _.set(driverVM, 'commercialDriver_WMIC', commercialDriverVM);
            _.set(driverVM, 'isExistingInPreviousPeriodDriver_WMIC', false);

            if (namedInsured) {
                WMICDriverUtil.setAssignment(driverVM);

                if (isDriverPNI) {
                    driverVM.creditConsentReceived = submissionVM.baseData.creditConsentReceived.value;
                    driverVM.creditConsentDate = submissionVM.baseData.creditConsentDate.value;
                    driverVM.personalInfoConsentForm = !_.isUndefined(
                        submissionVM.baseData.personalInfoConsentForm.value
                    )
                        ? submissionVM.baseData.personalInfoConsentForm.value
                        : null;
                    driverVM.creditInfoWithdrawalConsent = !_.isUndefined(
                        submissionVM.baseData.creditInfoWithdrawalConsent.value
                    )
                        ? submissionVM.baseData.creditInfoWithdrawalConsent.value
                        : null;
                }

                const ANI = _.find(
                    submissionVM.baseData.additionalNamedInsureds_WMIC.value,
                    namedInsuredParam =>
                        namedInsuredParam.contactPublicID === driverVM.person.publicID.value ||
                        namedInsuredParam.publicID === driverVM.person.publicID.value
                );

                if (ANI) {
                    driverVM.creditConsentReceived = ANI.creditConsentReceived;
                    driverVM.creditConsentDate = ANI.creditConsentDate;
                    driverVM.personalInfoConsentForm = !_.isUndefined(ANI.personalInfoConsentForm)
                        ? ANI.personalInfoConsentForm
                        : null;
                    driverVM.creditInfoWithdrawalConsent = !_.isUndefined(ANI.creditInfoWithdrawalConsent)
                        ? ANI.creditInfoWithdrawalConsent
                        : null;
                    driverVM.person.publicID = ANI.contactPublicID;
                }
            }

            submissionVM.lobData.personalAuto.coverables.drivers.value.push(driverVM.value);

            return driverVM;
        },
        [skeletonStruct, submissionVM, viewModelService, isBMSi, wizardStepDispatch]
    );

    const onCreateDriverWithModal = useCallback(
        (index, namedInsured, insuredRelation) =>
            showConfirm({
                title: quoteAndBindCommonMessages.revalidationRequiredTitle,
                message: quoteAndBindCommonMessages.revalidationRequiredBody,
                status: MODAL_CONSTANTS.STATUS.WARNING,
                icon: MODAL_CONSTANTS.ICON.ERROR,
                confirmButtonText: commonMessages.yes,
                cancelButtonText: commonMessages.cancelModel,
            }).then(results => {
                if (results === CONSTANTS.MODAL_RESULT.CANCEL || results === CONSTANTS.MODAL_RESULT.CLOSE) {
                    return false;
                }

                return onCreateDriver(index, namedInsured, insuredRelation);
            }),
        [onCreateDriver, showConfirm]
    );

    const saveDriver = useCallback(
        async selectedDriver => {
            _.unset(submissionVM.value, 'bindData');

            try {
                setWizardLoading(true, translator(messages.savingDriver));

                const driverId = selectedDriver.publicID ?? selectedDriver.tempID;

                const saveDriverResult = await LoadSaveService.saveDriver(driverId, submissionVM.value, authHeader);

                submissionVM.value = saveDriverResult;

                updateWizardData(submissionVM);
                setShowErrors(false);

                if (WMICValidationUtil.hasDtoValidationErrors(submissionVM, FlowStepId.PA_DRIVERS)) {
                    WMICVariousUtil.scrollToTop();
                }
                wizardStepDispatch({ type: 'reset', vm: submissionVM });
                return true;
            } catch (error) {
                WMICLogger.error('Save driver failed', error);
                WMICErrorHandler.processAsModal(parseErrorMessage(error)).catch(() => {
                    // do nothing
                });
                return false;
            } finally {
                setWizardLoading(false);
            }
            // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        },
        [setWizardLoading, translator, LoadSaveService, authHeader, submissionVM, wizardStepDispatch]
    );

    const onSaveDriver = useCallback(selectedDriver => saveDriver(selectedDriver.value), [saveDriver]);

    const onSaveDriverMiddleware = useCallback(
        selectedDriver => {
            if (driversWithDuplicateLicence) {
                const otherDriverWithSameLicense =
                    driversWithDuplicateLicence.find(d => d.publicID !== selectedDriver?.value?.publicID) ||
                    driversWithDuplicateLicence[0];

                showError({
                    title: translator(messages.licenseNotUnique),
                    message: translator(messages.licenceMatchMsg, { driver: otherDriverWithSameLicense.displayName }),
                });
            }
        },
        [driversWithDuplicateLicence, showError, translator]
    );

    const deleteDriver = useCallback(
        async driverId => {
            _.unset(submissionVM.value, 'bindData');

            try {
                setWizardLoading(true, translator(messages.removingDriver));

                const removeDriverResult = await LoadSaveService.removeDriver(submissionVM.value, driverId, authHeader);

                if (!_.isEmpty(removeDriverResult.errorMessages)) {
                    showError({
                        title: translator(messages.removingDriverErrorModalTitle),
                        message: removeDriverResult.errorMessages.join('\n'),
                    });
                    return false;
                }

                submissionVM.value = removeDriverResult.quoteData;

                updateWizardData(submissionVM);
                setShowErrors(false);

                if (WMICValidationUtil.hasDtoValidationErrors(submissionVM, FlowStepId.PA_DRIVERS)) {
                    WMICVariousUtil.scrollToTop();
                }

                return true;
            } catch (error) {
                WMICLogger.error('Remove driver failed', error);
                WMICErrorHandler.processAsModal(error, submissionVM.value.jobID, submissionVM.value.baseData.jobType);
                return false;
            } finally {
                setWizardLoading(false);
            }
            // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        },
        [setWizardLoading, translator, LoadSaveService, authHeader, showError, submissionVM]
    );

    const onDeleteDriver = useCallback(
        (driver, index) => {
            const modalMsg = translator(messages.removeDriver, { driver: driver.displayName?.value });

            showConfirm({
                title: messages.removeDriverTitle,
                message: modalMsg,
                confirmButtonText: commonMessages.yes,
                cancelButtonText: commonMessages.noModel,
            })
                .then(async response => {
                    if (response === CONSTANTS.MODAL_RESULT.CONFIRM) {
                        return deleteDriver(driver.publicID?.value);
                    }
                })
                .catch(_.noop);
        },
        [showConfirm, translator, deleteDriver]
    );

    const onCancelDriver = () => {
        _.set(submissionVM, 'isEditingPage.value', false);
        updateWizardData(submissionVM);
        wizardStepDispatch({ type: 'reset', vm: submissionVM });
    };

    const onNext = useCallback(async () => {
        if (!isComponentValid || !WMICDriverUtil.presentDriverList(submissionVM)) {
            setShowErrors(true);

            return false;
        }

        if (canEditSubmission) {
            try {
                setWizardLoading(true);

                submissionVM.value = await LoadSaveService.updateDraftSubmission(submissionVM.value, authHeader);

                updateWizardData(submissionVM);

                if (WMICValidationUtil.hasDtoValidationErrors(submissionVM, FlowStepId.PA_DRIVERS)) {
                    WMICVariousUtil.scrollToTop();
                    return false;
                }

                return submissionVM;
            } finally {
                setWizardLoading(false);
            }
        }

        return false;
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isComponentValid, canEditSubmission, setWizardLoading, LoadSaveService, submissionVM, authHeader]);

    const renderAddDriverButton = useCallback(
        ({ isEditing, onClick }) => {
            const drivers = _.get(submissionVM, `${RISK_PATHS.DRIVERS_PATH}.value`, []);
            const pni = _.get(submissionVM, 'baseData.primaryNamedInsured_WMIC.value');

            return (
                <DropdownMenuButton
                    id="addNewDriverButton"
                    buttonText={translator({
                        id: 'quoteandbind.pa.views.pa-drivers-details.addNewDriver',
                        defaultMessage: 'meta.Add New Driver',
                    })}
                    icon="mi-add"
                    disabled={isEditing}
                >
                    {WMICDriverUtil.canContactBeAddedToDrivers(drivers, pni) && (
                        <DropdownMenuLink onClick={() => onClick(drivers.length, pni, INSURED_ROLES.PRIMARY_INSURED)}>
                            {pni.displayName}
                        </DropdownMenuLink>
                    )}
                    {additionalNamedInsureds.map(
                        (ani, index) =>
                            WMICDriverUtil.canContactBeAddedToDrivers(drivers, ani.value) && (
                                <DropdownMenuLink
                                    onClick={() =>
                                        onClick(
                                            drivers.length,
                                            _.get(submissionVM, `${ANIS_PATH}.value[${index}]`),
                                            INSURED_ROLES.ADDITIONAL_INSURED
                                        )
                                    }
                                >
                                    {ani.displayName.value}
                                </DropdownMenuLink>
                            )
                    )}
                    <DropdownMenuLink
                        onClick={() => onClick(_.get(submissionVM, `${RISK_PATHS.DRIVERS_PATH}.value`, []).length)}
                    >
                        {translator(messages.addNewDriver)}
                    </DropdownMenuLink>
                </DropdownMenuButton>
            );
        },
        [additionalNamedInsureds, submissionVM, translator]
    );

    const overrideProps = {
        // TODO: set visibility to !policyChange and usCanadaYearsLicensedTypeKey === undefined
        reenterMsgContainer: {
            visible: false,
        },
        reenterMsg: {
            content: WMICRichTextUtil.translateRichText(translator(messages.reenterMsg, '')),
        },
        atLeastOneMsgContainer: {
            visible: _.isEmpty(_.get(submissionVM, `${RISK_PATHS.DRIVERS_PATH}.value`, [])),
        },
        atLeastOneMsg: {
            content: WMICRichTextUtil.translateRichText(translator(messages.atLeastOne)),
        },
        driverListView: {
            VMList: _.get(submissionVM, `${RISK_PATHS.DRIVERS_PATH}.children`, []),
            clickable: true,
            startOpen: true,
            readOnly: !canEditSubmission,
            VMData: [
                {
                    headerText: translator(messages.driverName),
                    path: 'displayName',
                },
                {
                    headerText: translator(messages.licenseNumber),
                    path: 'licenseNumber',
                },
                {
                    headerText: translator(messages.licenseProvince),
                    path: 'licenseState',
                    getData: (row, path) => _.get(row.value, path) || translator(messages.licenseProvinceOther),
                },
            ],
            toCreate: isBMSi ? onCreateDriverWithModal : onCreateDriver,
            onSave: onSaveDriver,
            onSaveMiddleware: onSaveDriverMiddleware,
            onDelete: onDeleteDriver,
            onCancel: onCancelDriver,
            toUndoCreate: () => {
                const drivers = _.get(submissionVM.value, RISK_PATHS.DRIVERS_PATH);

                drivers.splice(drivers.length - 1, 1);
                _.set(submissionVM.value, RISK_PATHS.DRIVERS_PATH, drivers);
                updateWizardData(submissionVM);
            },
            detailViewComponent: WMICDriverDetailView,
            onValidate,
            renderAddButton: renderAddDriverButton,
            detailViewComponentProps: {
                jobVM: submissionVM,
            },
        },
        requiredText: {
            visible: _.get(submissionVM, RISK_PATHS.DRIVERS_PATH).length > 0,
        },
    };

    const resolvers = {
        resolveComponentMap: {
            WMICRequiredText,
        },
    };

    return (
        <WMICWizardSubmissionPage
            onNext={onNext}
            skipWhen={initialValidation}
            disableNext={!isComponentValid}
            cancelLabel={translator(quoteAndBindCommonMessages.saveAndExit)}
            showRequired
            flowStepId={FlowStepId.PA_DRIVERS}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                onModelChange={updateWizardData}
                overrideProps={overrideProps}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                showErrors={showErrors}
            />
        </WMICWizardSubmissionPage>
    );
}

WMICDriversDetailsPage.propTypes = wizardProps;

export default WMICDriversDetailsPage;
